import { useEffect, useRef } from 'react';
import { subscribeToChannel, type DuplexerEventName, type DuplexerPayload } from './duplexer-connection';

export type { DuplexerPayload } from './duplexer-connection';

const DEFAULT_EVENTS: DuplexerEventName[] = ['updated', 'action'];

/**
 * Subscribe to an `individuals-*` duplexer channel over the app's ONE shared duplexer connection
 * (lib/duplexer-connection.ts) — every channel and every listener is multiplexed onto a single Duplexer +
 * VirtualSocket, instead of each hook instance opening its own WebSocket and minting its own instance.
 * HTTPS-only (see the shared module); a no-op on plain http (local dev runs --experimental-https).
 *
 * New channels build on this helper rather than opening a connection themselves.
 */
export function useDuplexerSubscription<T>(
  channel: string,
  onPayload: (payload: DuplexerPayload<T>) => void,
  events: DuplexerEventName[] = DEFAULT_EVENTS,
) {
  const onPayloadRef = useRef(onPayload);
  onPayloadRef.current = onPayload;

  const eventsKey = events.join(',');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.protocol !== 'https:') return;
    return subscribeToChannel(channel, eventsKey.split(',') as DuplexerEventName[], (payload) =>
      onPayloadRef.current(payload as DuplexerPayload<T>),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, eventsKey]);
}
